import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CONSENT_CHANGE_EVENT, canLoadAnalytics } from "@/lib/utils/consent";
import {
  buildPageViewEvent,
  getAnalyticsConfiguration,
  loadGoogleTag,
  loadGtmContainer,
  removeAnalyticsInjectedScripts,
  resetAnalyticsTrackingState,
  setAnalyticsRuntimeDisabled,
  trackPageViewEvent,
} from "@/lib/utils/gtm";

function readAnalyticsConsent(): boolean {
  return canLoadAnalytics();
}

export function AnalyticsManager() {
  const location = useLocation();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(readAnalyticsConsent);
  const lastPageViewSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleConsentChange = () => {
      setAnalyticsEnabled(readAnalyticsConsent());
    };

    window.addEventListener(CONSENT_CHANGE_EVENT, handleConsentChange as EventListener);
    window.addEventListener("storage", handleConsentChange);

    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, handleConsentChange as EventListener);
      window.removeEventListener("storage", handleConsentChange);
    };
  }, []);

  useEffect(() => {
    if (!analyticsEnabled) {
      lastPageViewSignatureRef.current = null;
      removeAnalyticsInjectedScripts();
      setAnalyticsRuntimeDisabled(true);
      resetAnalyticsTrackingState();
      return;
    }

    setAnalyticsRuntimeDisabled(false);

    const { gtmContainerId, gaMeasurementId } = getAnalyticsConfiguration();
    if (gtmContainerId) {
      loadGtmContainer(gtmContainerId);
    } else if (gaMeasurementId) {
      loadGoogleTag(gaMeasurementId);
    }

    let cancelled = false;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (cancelled) return;
        const pageViewEvent = buildPageViewEvent(location, document.title);
        const signature = JSON.stringify(pageViewEvent);

        if (lastPageViewSignatureRef.current === signature) {
          return;
        }

        lastPageViewSignatureRef.current = signature;
        trackPageViewEvent(pageViewEvent);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [analyticsEnabled, location]);

  return null;
}
